import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { logout } from '../../store/authSlice';

export default function Navbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(s => s.auth);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    background: 'rgba(15,15,26,0.95)', backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(99,102,241,0.2)',
    padding: '0 24px', height: '64px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };

  const logoStyle = {
    display: 'flex', alignItems: 'center', gap: '10px',
    textDecoration: 'none', color: '#fff',
  };

  const linkStyle = (active) => ({
    color: active ? '#6366f1' : '#94a3b8',
    textDecoration: 'none', fontSize: '14px', fontWeight: '500',
    padding: '6px 12px', borderRadius: '8px',
    background: active ? 'rgba(99,102,241,0.1)' : 'transparent',
    transition: 'all 0.2s',
  });

  const btnStyle = {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff', border: 'none', borderRadius: '10px',
    padding: '8px 18px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', textDecoration: 'none',
  };

  return (
    <nav style={navStyle}>
      {/* Logo */}
      <Link to="/" style={logoStyle}>
        <div style={{
          width: 36, height: 36, borderRadius: '10px',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '18px',
        }}>🚗</div>
        <span style={{ fontWeight: '800', fontSize: '18px' }}>
          Smart<span style={{ color: '#6366f1' }}>Park</span>
        </span>
        <span style={{ fontSize: '11px', color: '#6366f1', background: 'rgba(99,102,241,0.1)', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>CSN</span>
      </Link>

      {/* Desktop Nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="desktop-nav">
        <Link to="/" style={linkStyle(isActive('/'))}>Home</Link>
        <Link to="/map" style={linkStyle(isActive('/map'))}>Find Parking</Link>
        {isAuthenticated && <Link to="/dashboard" style={linkStyle(isActive('/dashboard'))}>Dashboard</Link>}
        {isAuthenticated && user?.role === 'admin' && (
          <Link to="/admin" style={linkStyle(isActive('/admin'))}>Admin</Link>
        )}
      </div>

      {/* Auth Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {isAuthenticated ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: '700',
              }}>
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <span style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>
                {user?.name?.split(' ')[0]}
              </span>
            </div>
            <button onClick={handleLogout} style={{
              background: 'rgba(239,68,68,0.1)', color: '#ef4444',
              border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px',
              padding: '6px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}>
              Logout
            </button>
          </div>
        ) : (
          <>
            <Link to="/login" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '14px', fontWeight: '500' }}>
              Login
            </Link>
            <Link to="/register" style={btnStyle}>Get Started</Link>
          </>
        )}

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: 'none', background: 'none', border: 'none',
            color: '#fff', fontSize: '24px', cursor: 'pointer',
          }}
          className="mobile-menu-btn"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              position: 'absolute', top: '64px', left: 0, right: 0,
              background: '#1a1a2e', borderBottom: '1px solid rgba(99,102,241,0.2)',
              padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px',
            }}
          >
            <Link to="/" style={linkStyle(isActive('/'))} onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/map" style={linkStyle(isActive('/map'))} onClick={() => setMenuOpen(false)}>Find Parking</Link>
            {isAuthenticated && <Link to="/dashboard" style={linkStyle(isActive('/dashboard'))} onClick={() => setMenuOpen(false)}>Dashboard</Link>}
            {isAuthenticated && user?.role === 'admin' && <Link to="/admin" style={linkStyle(isActive('/admin'))} onClick={() => setMenuOpen(false)}>Admin</Link>}
            {!isAuthenticated && <Link to="/login" style={linkStyle(false)} onClick={() => setMenuOpen(false)}>Login</Link>}
            {!isAuthenticated && <Link to="/register" style={{ ...btnStyle, textAlign: 'center' }} onClick={() => setMenuOpen(false)}>Get Started</Link>}
            {isAuthenticated && <button onClick={handleLogout} style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '8px 14px', fontSize: '14px', cursor: 'pointer' }}>Logout</button>}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </nav>
  );
}